import React from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { DateTime } from 'luxon';
import { REGISTRATION_STATUS } from '@root/src/types';
import getRoute from '@services/routeList';
import { textH2, textPBold } from '@features/ui/styles/textStyles';
import BannerLogin from '@features/users/components/BannerLogin';
import EventCategoryList from '@features/eventDetail/components/EventCategoryList';
import { selectEventCategoriesIds, selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import { selectIsAuthenticated } from '@features/users/slices/userSlice';
import EventTimeSentence, { EventTimeContext } from '@features/eventDetail/components/EventTimeSentence';
import { getQueryEventId } from '@features/eventDetail/utils';
import Button, { BUTTON_VARIANT } from '@features/ui/components/Button';
import { ICON_NAME } from '@features/ui/components/Icon';
import Banner from '@features/ui/components/Banner';
import EventSubscriptionForSelect from './EventSubscriptionForSelect';

const EventSubscription = () => {
  const { t } = useTranslation();
  const router = useRouter();

  const eventId = getQueryEventId(router.query);
  const event = useSelector(selectEventDetail(eventId));
  const categories = useSelector(selectEventCategoriesIds(eventId));
  const logged = useSelector(selectIsAuthenticated);

  const startDate = event.status.registrationFrom;
  const endDate = event.status.registrationTo;
  const showTimeSentence = [REGISTRATION_STATUS.NOT_YET_OPEN, REGISTRATION_STATUS.OPEN, REGISTRATION_STATUS.CLOSED]
    .includes(event.status.registration);

  const eventRegistrationStatus = event.status.registration;
  const showBanner = !logged && categories.length > 0 && eventRegistrationStatus === REGISTRATION_STATUS.OPEN;
  const showBannerEventIsInASerie = event.serie.isChampionship;

  return (
    <Container>
      <Title>
        {t('eventSubscription_title.message')}
      </Title>
      {showTimeSentence && (
        <EventTimeSentence
          startDate={startDate}
          endDate={endDate}
          context={EventTimeContext.SUBSCRIPTION}
          extraVars={
            {
              openHour: DateTime.fromISO(startDate).toFormat('HH\'h\'mm'),
              closeHour: DateTime.fromISO(endDate).toFormat('HH\'h\'mm'),
            }
          }
        />
      )}
      {showBanner && <BannerLogin />}
      {showBannerEventIsInASerie && (
        <StyledBanner>
          <BannerContent>
            <div>
              {t('eventSubscription_bannerEventIsInSerie.message', {
                eventName: event.name,
                serieName: event.serie.name,
              })}
            </div>
            <Button
              variant={BUTTON_VARIANT.secondary}
              text={t('eventSubscription_buttonGoToSerie.message')}
              leftIcon={ICON_NAME.emojiEvent}
              onClick={() => router.push(getRoute.series(event.serie.slug).subscriptions(), null)}
            />
          </BannerContent>
        </StyledBanner>
      )}
      {logged && event.status.registration === REGISTRATION_STATUS.OPEN && <EventSubscriptionForSelect />}
      {event.status.registration !== REGISTRATION_STATUS.NOT_YET_OPEN && <EventCategoryList eventId={eventId} />}
    </Container>
  );
};

export default EventSubscription;

const StyledBanner = styled(Banner)`
  margin-top: 20px;
  margin-bottom: 20px;
  width: 100%;
`;
const BannerContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  justify-content: space-between;
  >:first-child {
    ${textPBold};
    margin-bottom: 20px;
  }
`;
const Title = styled.h2`
  ${textH2};
  margin-bottom: 20px;
`;
const Container = styled.div`
  width: 100%;
`;
